import { recoverValuesFromSearchParams } from '@components/Crud/useFilter';
import { useList } from '@components/Hooks/useList';
import { getListOrDefault, isLoading } from '@components/Hooks/useList.utils';
import { fetcher } from '@components/Hooks/useListActions';
import { useEffect, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useLocation } from 'react-router-dom';
import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import useProductActionsStore from '../../../../../stores/ProductActionsStore/productActions.store';
import useProductDetailsStore from '../../../../../stores/ProductDetails/productDetails.store';
import { INFINITE_SCROLL_ROOT_MARGIN } from '../../../../../utils/constants';
import { formatCompleteName } from '../../../../../utils/formatters';
import { User } from '../../../../user-admin/user.types';
import type { SelectModalListItem } from '../../SelectModalList/selectModalList.types';

const defaultUsersPath = 'admin/users';
const SEARCH_DEBOUNCE_MS = 400;

type UseUserSelectModalParams = {
  open: boolean;
  onClick: (user: User) => void;
};

const useUserSelectModal = ({ open, onClick }: UseUserSelectModalParams) => {
  const location = useLocation();
  const resetFilterWithOrders = useProductDetailsStore((state) => state.resetFilterWithOrders);
  const resetProductActionsStore = useProductActionsStore(
    (state) => state.resetProductActionsStore,
  );

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const { state, onSearch, next } = useList<User, string>(fetcher(defaultUsersPath), {
    order: { name: 'ASC' },
  });

  const users = getListOrDefault<User>(state);
  const isListLoading = isLoading(state);
  const hasNextPage = state.tag === 'with-data' && state.hasMore;

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: isListLoading,
    hasNextPage,
    onLoadMore: next,
    rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
  });

  useEffect(() => {
    if (!open || searchTerm !== debouncedSearchTerm) {
      return;
    }

    const params = debouncedSearchTerm
      ? { nameOrEmail: debouncedSearchTerm }
      : recoverValuesFromSearchParams(location.search);

    onSearch(1, params);
  }, [open, searchTerm, debouncedSearchTerm, location.search, onSearch]);

  const resetSearchTerm = () => setSearchTerm('');

  const handleSelectUser = (user: User) => {
    onClick(user);
    resetFilterWithOrders();
    resetProductActionsStore();
  };

  const items: SelectModalListItem[] =
    users?.map((user) => ({
      id: user.id,
      primary: formatCompleteName(user.name, user.surname),
      secondary: user.email,
      avatarSrc: user.profileUrl,
      avatarInitials: (user.name?.[0] || user.email?.[0] || 'T').toUpperCase(),
      onSelect: () => handleSelectUser(user),
    })) ?? [];

  return {
    items,
    isListLoading,
    hasNextPage,
    infiniteRef,
    rootRef,
    searchTerm,
    setSearchTerm,
    resetSearchTerm,
  };
};

export default useUserSelectModal;
